import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useFormik } from 'formik';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Logo from '../assets/logo.png';
import useErrors from '../hooks/useErrors';
import server from '../services/server';
import { useAuth } from '../context/AuthContext';

const HERO_IMAGE =
    'https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=2000&q=80';

function AdminLoginPanel() {
    const theme = useTheme();
    const { setErrors } = useErrors();
    const { Login } = useAuth();

    const { values, handleSubmit, handleChange, setFieldValue, isSubmitting } = useFormik({
        initialValues: {
            username: '',
            password: '',
            security: true,
        },
        onSubmit: async formValues => {
            try {
                const { data } = await server.post('/auth', {
                    username: formValues.username,
                    password: formValues.password,
                    profile: 'admin',
                });
                Login({
                    token: data.data.token,
                    refreshtoken: data.data.refreshToken,
                    user: data.data.user,
                });
            } catch (error) {
                setErrors(error);
            }
        },
    });

    return (
        <Card elevation={0} variant="outlined" component="form" onSubmit={handleSubmit} noValidate>
            <CardHeader
                sx={{
                    pb: 0,
                    pt: 2.5,
                    px: 2.5,
                    '& .MuiCardHeader-title': { fontSize: '0.9375rem', fontWeight: 600, color: 'grey.900' },
                    '& .MuiCardHeader-subheader': {
                        mt: 0.5,
                        fontSize: '0.8125rem',
                        lineHeight: 1.45,
                        color: 'grey.600',
                    },
                }}
                title="Entrar no painel administrativo"
                subheader="Acesso restrito à equipe Eurobem."
            />
            <CardContent sx={{ pt: 2, px: 2.5, pb: 2.5 }}>
                <Stack spacing={2}>
                    <TextField
                        required
                        fullWidth
                        name="username"
                        label="Usuário"
                        value={values.username}
                        onChange={handleChange}
                        autoComplete="username"
                    />
                    <TextField
                        required
                        fullWidth
                        name="password"
                        type={values.security ? 'password' : 'text'}
                        label="Senha"
                        value={values.password}
                        onChange={handleChange}
                        autoComplete="current-password"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setFieldValue('security', !values.security)}
                                        edge="end"
                                        aria-label="Alternar visibilidade da senha"
                                    >
                                        {values.security ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button type="submit" variant="contained" size="large" fullWidth disabled={isSubmitting} sx={{ mt: 1, py: 1.25 }}>
                        Entrar
                    </Button>
                    <Typography variant="caption" sx={{ color: 'grey.500', textAlign: 'center', display: 'block' }}>
                        <Link to="/recuperar-senha?profile=admin" style={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                            Esqueci minha senha
                        </Link>
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );
}

function ProfileSelectionPanel({ onShowAdmin }) {
    return (
        <Stack spacing={2}>
            <Button
                component={Link}
                to="/clube/login"
                variant="contained"
                size="large"
                fullWidth
                sx={{ py: 1.35, fontWeight: 700 }}
            >
                Sou gestor de clube
            </Button>
            <Button
                component={Link}
                to="/anunciante/login"
                variant="outlined"
                size="large"
                fullWidth
                sx={{ py: 1.35, fontWeight: 700 }}
            >
                Sou anunciante
            </Button>
            <Typography variant="caption" sx={{ color: 'grey.500', textAlign: 'center', display: 'block', pt: 0.5 }}>
                Equipe Eurobem?{' '}
                <Button
                    variant="text"
                    size="small"
                    onClick={onShowAdmin}
                    sx={{
                        p: 0,
                        minWidth: 0,
                        verticalAlign: 'baseline',
                        fontWeight: 600,
                        fontSize: 'inherit',
                        textTransform: 'none',
                    }}
                >
                    Acesso administrativo
                </Button>
            </Typography>
        </Stack>
    );
}

export default function Auth() {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const [showAdminLogin, setShowAdminLogin] = useState(false);

    const overlayGradient = `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.82)} 0%, ${alpha('#0f172a', 0.75)} 50%, ${alpha('#020617', 0.88)} 100%)`;

    const heroPanel = (
        <Box
            sx={{
                position: 'relative',
                flex: { xs: 'none', md: '0 0 50%' },
                width: { xs: '100%', md: '50%' },
                minHeight: { xs: 192, sm: 220, md: '100vh' },
                backgroundImage: `${overlayGradient}, url(${HERO_IMAGE})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                p: { xs: 3, md: 5 },
                color: 'common.white',
            }}
        >
            <Typography
                variant={isMdUp ? 'h3' : 'h5'}
                sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    letterSpacing: '-0.02em',
                    textShadow: '0 2px 24px rgba(0,0,0,0.35)',
                    maxWidth: 440,
                }}
            >
                {showAdminLogin ? 'Painel administrativo' : 'Portal Eurobem'}
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    mt: 2,
                    maxWidth: 400,
                    opacity: 0.94,
                    lineHeight: 1.65,
                    fontWeight: 500,
                    textShadow: '0 1px 12px rgba(0,0,0,0.25)',
                }}
            >
                {showAdminLogin
                    ? 'Gestão central de clubes, contratos e operação da plataforma.'
                    : 'Escolha como deseja acessar: gestão do seu clube de benefícios ou painel do anunciante.'}
            </Typography>
        </Box>
    );

    return (
        <Stack direction={{ xs: 'column', md: 'row' }} minHeight={{ md: '100vh' }} sx={{ bgcolor: '#fafafa' }}>
            {heroPanel}

            <Box
                sx={{
                    flex: { xs: '1 1 auto', md: '0 0 50%' },
                    width: { xs: '100%', md: '50%' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    py: { xs: 4, md: 5 },
                    px: { xs: 2.5, sm: 5, md: 6 },
                }}
            >
                <Stack spacing={3} sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
                    <Box component="img" src={Logo} alt="Eurobem" sx={{ width: 136, alignSelf: 'flex-start' }} />

                    <Stack spacing={0.75}>
                        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em', color: 'grey.900' }}>
                            {showAdminLogin ? 'Login administrativo' : 'Como você deseja entrar?'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'grey.600', lineHeight: 1.55 }}>
                            {showAdminLogin
                                ? 'Informe usuário e senha da equipe Eurobem.'
                                : 'Selecione o perfil para ir ao login correspondente.'}
                        </Typography>
                    </Stack>

                    {showAdminLogin ? (
                        <AdminLoginPanel />
                    ) : (
                        <ProfileSelectionPanel onShowAdmin={() => setShowAdminLogin(true)} />
                    )}

                    {showAdminLogin && (
                        <Typography variant="caption" sx={{ color: 'grey.500', textAlign: 'center', display: 'block' }}>
                            <Button
                                variant="text"
                                size="small"
                                onClick={() => setShowAdminLogin(false)}
                                sx={{
                                    color: 'grey.600',
                                    fontWeight: 500,
                                    fontSize: 'inherit',
                                    textTransform: 'none',
                                }}
                            >
                                Voltar para seleção de perfil
                            </Button>
                        </Typography>
                    )}
                </Stack>
            </Box>
        </Stack>
    );
}
